"use strict";

function asText(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(" ");
  return String(value).trim();
}

export function caseNarrative(payload = {}) {
  return [
    payload.text,
    payload.description,
    payload.case_description,
    payload.narrative,
    payload.caseText,
    payload.caseDescription,
  ]
    .map(asText)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function deriveFundLifecycleScope(payload = {}) {
  const text = caseNarrative(payload);
  const terms = [];
  if (/\bsubscriptions?\b|\bsubscribe\b/.test(text)) terms.push("subscriptions");
  if (/\bredemptions?\b|\bredeem\b/.test(text)) terms.push("redemptions");
  if (/\bcapital calls?\b/.test(text)) terms.push("capital calls");
  if (/\bdistributions\b|\bdistribution payments?\b/.test(text)) terms.push("distributions");
  const unique = Array.from(new Set(terms));
  const phrase = unique.length === 0
    ? "fund-unit lifecycle events"
    : unique.length === 1
      ? unique[0]
      : `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
  return { terms: unique, phrase };
}

export function humanizeReportInputLabel(value, kind = "generic") {
  const raw = asText(value);
  const key = raw.toLowerCase();
  const maps = {
    custody: {
      "": "not yet decided",
      unknown: "not yet decided",
      "not-decided": "not yet decided",
      "regulated-custodian": "regulated custodian",
      "ta/csd": "transfer agent or CSD",
      "self-custody": "self-custody wallet model",
    },
    venue: {
      "": "not yet decided",
      unknown: "not yet decided",
      "csd/ta-registered": "CSD or transfer-agent record environment",
      "ats/mtf": "ATS or MTF",
      dex: "decentralized exchange",
      otc: "controlled OTC transfers",
    },
    settlement: {
      "": "not yet decided",
      unknown: "not yet decided",
      "not-decided": "not yet decided",
      "not-material-now": "not material at this stage",
      "traditional-fiat-rails": "traditional fiat payment rails",
      "custodian-bank-settlement": "custodian or bank settlement",
      "stablecoin-tokenized-cash": "stablecoin or tokenized cash",
      "deposit-token-tokenized-deposit": "bank deposit token, tokenized deposit or institutional DvP rail",
      "t+0 atomic": "T+0 or atomic DvP target",
      "t+1/2": "T+1/T+2 securities settlement",
      "off-chain": "off-chain booking and payment",
    },
    liquidity: {
      "": "not yet decided",
      unknown: "not yet decided",
      none: "no secondary liquidity planned",
      "ad-hoc otc": "ad-hoc controlled OTC transfers",
      quarterly: "quarterly transfer windows",
      continuous: "continuous transfer intent",
    },
    disclosure: {
      "": "not yet decided",
      unknown: "not yet decided",
      exempt: "exemption-based disclosure route",
      prospectus: "prospectus route",
      kid: "key information document route",
    },
  };
  return maps[kind]?.[key] || raw || "not yet decided";
}

export function findRenderedReportDefects(textValue, payload = {}) {
  const text = asText(textValue);
  const lower = text.toLowerCase();
  const narrative = caseNarrative(payload);
  const defects = [];

  if (text.length < 500) {
    defects.push("rendered_report_missing_or_too_short");
  }

  if ((/\bcommercial paper\b|\bcp program(?:me)?\b|\bcp notes?\b/.test(narrative)) &&
      !/\bbond programme\b/.test(narrative) &&
      /\bconventional bond programme\b|\bexisting bond programme\b/.test(lower)) {
    defects.push("commercial_paper_as_bond_programme");
  }

  if (/money[- ]market fund|\bmmf\b|short-duration treasury fund/.test(narrative) &&
      !/capital calls?/.test(narrative) &&
      /capital calls?|kapitalabruf|kapitalindkald|kapitalkald|appels? de capitaux|llamadas? de capital|chamadas? de capital|资本调用/.test(lower)) {
    defects.push("money_market_fund_capital_calls");
  }

  const custody = asText(payload?.custodyModel || payload?.custody_model).toLowerCase();
  if ((!custody || /unknown|not[- ]decided|select/.test(custody)) &&
      /conservative custody(?:\s*\/\s*| and )settlement choices/.test(lower)) {
    defects.push("unsupported_conservative_custody");
  }

  const liquidity = asText(
    payload?.liquidityCadence ||
    payload?.secondaryMarketLiquidity ||
    payload?.secondary_market_liquidity
  ).toLowerCase();
  if (liquidity && !/^(?:unknown|not[- ]decided|select)$/.test(liquidity) &&
      /transfer or liquidity cadence is not stated/.test(lower)) {
    defects.push("structured_liquidity_reported_as_missing");
  }

  const exactDefects = [
    ["thin_disclosure_grammar", /thin disclosure architecture make\b/i],
    ["capitalized_mid_sentence", /cannot yet show that The proposed\b/],
    ["distribution_support_grammar", /capable of distribution support pool-performance/i],
    ["internal_custody_label", /Selected custody-design label:/i],
    ["internal_transfer_environment_label", /Generic launch transfer-environment label:/i],
  ];
  for (const [code, pattern] of exactDefects) {
    if (pattern.test(text)) defects.push(code);
  }

  for (const internalValue of [
    "traditional-fiat-rails",
    "stablecoin-tokenized-cash",
    "deposit-token-tokenized-deposit",
    "not-decided",
  ]) {
    if (lower.includes(internalValue)) defects.push(`internal_enum:${internalValue}`);
  }

  const missingCashEvidence =
    /no commitment|no committed|not committed|no finality analysis|no complete recovery|not yet (?:tested|validated)|no audited production/.test(narrative);
  // Only treat an Advanced/Strong readiness statement as a cash-leg claim when
  // the readiness wording is explicitly tied to the cash leg. The previous
  // whole-report pattern could incorrectly match unrelated phrases such as
  // “operational readiness is assessed as Advanced” in cases that also stated
  // missing cash-leg evidence elsewhere in the input.
  const overstatedReadiness =
    /cash[- ]leg[^.!?\n]{0,220}(?:readiness[^.!?\n]{0,80})?(?:advanced|strong|avanceret|stærk|fortgeschritten|stark|avancée|forte|avanzada|fuerte|avançada|高级|先进|强)/.test(lower) ||
    /(?:advanced|strong|avanceret|stærk|fortgeschritten|stark|avancée|forte|avanzada|fuerte|avançada|高级|先进|强)[^.!?\n]{0,120}cash[- ]leg/.test(lower);
  if (missingCashEvidence && overstatedReadiness) {
    defects.push("cash_leg_readiness_overstated");
  }

  return Array.from(new Set(defects));
}

export function assertRenderedReportQuality(textValue, payload = {}) {
  const defects = findRenderedReportDefects(textValue, payload);
  if (defects.length === 0) return true;

  // Only structural render failure is allowed to block report export.
  // Language- and phrase-based quality checks remain diagnostic only because
  // legitimate report wording can otherwise create false-positive hard stops.
  const blockingDefects = defects.filter((code) => code === "rendered_report_missing_or_too_short");
  const diagnosticDefects = defects.filter((code) => !blockingDefects.includes(code));
  if (diagnosticDefects.length > 0) {
    console.warn("TVAC rendered report quality diagnostics:", diagnosticDefects);
  }
  if (blockingDefects.length === 0) return true;

  const error = new Error(`TVAC rendered report quality failure: ${blockingDefects.join(", ")}`);
  error.code = "TVAC_RENDERED_REPORT_QUALITY_FAILURE";
  error.details = blockingDefects;
  throw error;
}
